import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { User } from '../../domain/entities/User';
import { appLanguage } from '@shared/infrastructure/i18n/appLanguage';

/**
 * Estado de autenticación en Zustand
 * Esta es la capa de infraestructura para el manejo del estado
 */
interface AuthState {
  // Estado de la Sesión
  isLoggedIn: boolean;
  user: User | null;
  isLoading: boolean;
  jwtToken: string | null;
  isAuthReady: boolean; // true cuando Firebase ha resuelto el estado inicial

  // Acciones
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  updatePhotoUrl: (photoUrl: string) => void;
  updateProfile: (data: { name: string; surname: string; secondSurname?: string }) => void;
  setAuthReady: () => void;
}

/**
 * Store de autenticación con persistencia en localStorage
 * Mantiene el estado de sesión del usuario
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isLoggedIn: false,
      user: null,
      isLoading: false,
      jwtToken: null,
      isAuthReady: false,

      setAuth: (user, token) => {
        set({
          isLoggedIn: true,
          user,
          jwtToken: token,
          isLoading: false,
        });
      },

      clearAuth: () => {
        set({
          isLoggedIn: false,
          user: null,
          jwtToken: null,
          isLoading: false,
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setAuthReady: () => set({ isAuthReady: true }),

      updatePhotoUrl: (photoUrl) => {
        const current = get().user;
        if (current) {
          const primitives = current instanceof User
            ? current.toPrimitives()
            : (current as any);
          set({ user: User.fromPrimitives({ ...primitives, photoUrl }) });
        }
      },

      updateProfile: ({ name, surname, secondSurname }) => {
        const current = get().user;
        if (current) {
          const primitives = current instanceof User
            ? current.toPrimitives()
            : (current as any);
          set({ user: User.fromPrimitives({ ...primitives, name, surname, secondSurname: secondSurname ?? primitives.secondSurname }) });
        }
      },
    }),
    {
      name: 'klonup-auth',
      partialize: (state) => ({
        user: state.user
          ? state.user instanceof User
            ? state.user.toPrimitives()
            : (state.user as any)
          : null,
        jwtToken: state.jwtToken,
        isLoggedIn: state.isLoggedIn,
        // photoUrl no se persiste — siempre viene de Firebase vía onAuthStateChanged
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          const userData = state.user as any;
          state.user = User.fromPrimitives(userData);
        }
      },
    }
  )
);

// Sincroniza photoUrl desde Firebase y restaura el idioma guardado cuando Firebase recupera la sesión
onAuthStateChanged(auth, async (firebaseUser) => {
  if (firebaseUser?.photoURL) {
    useAuthStore.getState().updatePhotoUrl(firebaseUser.photoURL);
  }

  // setAuthReady primero — no bloqueamos la carga de la app
  useAuthStore.getState().setAuthReady();

  // Restaurar idioma desde Firestore en segundo plano (refresh, nueva pestaña, nuevo navegador)
  // Evita depender solo del caché de LanguageDetector — si localStorage fue borrado o cambió el navegador,
  // el idioma guardado en Firestore se aplica igualmente
  if (firebaseUser) {
    firebaseUser.getIdToken().then(async (token) => {
      try {
        const { environment } = await import('@/config/environment');
        const res = await fetch(`${environment.apiUrl}/v2/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.language) {
            const { default: i18n } = await import('@shared/infrastructure/i18n/config');
            const lang = data.language.startsWith('en') ? 'en' : 'es';
            if (appLanguage(i18n) !== lang) {
              await i18n.changeLanguage(lang);
            }
          }
        }
      } catch {
        // No bloquear la carga de la app si falla la restauración del idioma
      }
    }).catch(() => {});
  }
});
